<template>
  <div class="role-service-provider-form">
    <!-- Role RoleServiceProviderMachine -->
    <draggable
      :list="roleServiceProviderMachines"
      item-key="id"
      handle=".drag-handle"
      @end="onDragEnd"
      ref="draggableRef"
    >
      <template #item="{ element }">
        <RoleServiceProviderMachineComponent
          :model-value="element"
          :selectable-machines="selectableMachines"
          @update:model-value="roleServiceProviderMachineController.update"
          @delete="roleServiceProviderMachineController.delete"
        />
      </template>
    </draggable>

    <!-- New RoleServiceProviderMachine -->
    <RoleServiceProviderMachineComponent
      v-if="newRoleServiceProviderMachine !== null"
      :model-value="newRoleServiceProviderMachine"
      :selectable-machines="selectableMachines"
      @update:model-value="createRoleServiceProviderMachine"
      @delete="onNewServiceProviderMachineButtonClicked"
      ref="newRoleServiceProviderMachineComponentRef"
    />
    <GeneralButton
      size="small"
      @click="onNewServiceProviderMachineButtonClicked"
      :icon="['fas', 'plus']"
      class="mb-2"
    >
      Machine toevoegen
    </GeneralButton>
    <div>
      <label>
        <span class="sr-only block text-sm">Locatie</span>
        <InputCombobox
          v-model="modelValue.location"
          :items="tenantBaseLocations"
          :disable-control="$attrs.disabled as boolean"
          items-key-name="id"
          items-value-name="name"
          :creation-model-type="Location"
          input-placeholder="Locatie"
          @item-selected="updateRole"
          @item-deselected="updateRole"
          return-null-if-no-matches
        />
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from "vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import { storeToRefs } from "pinia"
import { useLocationStore } from "@/store/locations.store"
import { RoleServiceProvider } from "@/models/role/role-service-provider.model"
import { Location } from "@/models/location.model"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import draggable from "vuedraggable"
import { RoleServiceProviderMachine } from "@/models/role-service-provider-machine.model.ts"
import { ModelFactory } from "@/factories/model-factory.ts"
import RoleServiceProviderMachineComponent from "@/components/role/RoleServiceProviderMachineComponent.vue"
import type { RoleServiceProviderMachineController } from "@/controllers/model-controllers/role-service-provider-machine-controller.ts"
import type { IRoleServiceProviderMachine } from "@/interfaces/api/role-service-provider-machine.interface.ts"
import { useMachineStore } from "@/store/machines.store.ts"
import { useRoleStore } from "@/store/roles.store.ts"

const modelValue = defineModel<RoleServiceProvider>({ required: true })
const emit = defineEmits(["update:modelValue", "roleChanged"])

const roleServiceProviderMachines = ref<RoleServiceProviderMachine[]>([])
// Keep the role service provider machines sorted by sortIndex
watch(
  () => modelValue.value.roleServiceProviderMachines.map((rspm) => rspm.sortIndex),
  async () => {
    await nextTick()
    roleServiceProviderMachines.value = [...modelValue.value.roleServiceProviderMachines].sort(
      (a, b) => a.sortIndex - b.sortIndex
    )
  },
  { immediate: true }
)

const locationStore = useLocationStore()
const { tenantBaseLocations } = storeToRefs(locationStore)

const machineStore = useMachineStore()
const { machines } = storeToRefs(machineStore)
const selectableMachines = computed(() => {
  return machines.value.filter((machine) => {
    return !roleServiceProviderMachines.value.some((rspm) => rspm.machine?.id === machine.id)
  })
})

const roleServiceProviderMachineController: RoleServiceProviderMachineController = inject(
  "roleServiceProviderMachineController"
)!
const newRoleServiceProviderMachine = ref<RoleServiceProviderMachine | null>(null)
/**
 * Handles the end of a drag event to update the sort order of role service provider machines.
 */
function onDragEnd(e: any) {
  const { oldIndex, newIndex } = e

  if (oldIndex === newIndex) {
    return // no change in order
  }

  // figure out the slice that shifted
  const start = Math.min(oldIndex, newIndex)
  const end = Math.max(oldIndex, newIndex)

  // only re-assign & persist those
  for (let i = start; i <= end; i++) {
    roleServiceProviderMachines.value[i].sortIndex = i
    roleServiceProviderMachineController.update(roleServiceProviderMachines.value[i])
  }
}

const roleStore = useRoleStore()
function updateRole() {
  roleStore.updateRole(modelValue.value)
  emit("roleChanged")
}

const draggableRef = ref()
async function createRoleServiceProviderMachine(
  roleServiceProviderMachine: RoleServiceProviderMachine
) {
  await roleServiceProviderMachineController.create(roleServiceProviderMachine)
  newRoleServiceProviderMachine.value = null

  // Focus the purchase item combobox just after creation
  await nextTick()
  const roleServiceProviderMachineComponents = draggableRef.value.componentStructure.children
  const lastDraggableChild =
    roleServiceProviderMachineComponents[roleServiceProviderMachineComponents.length - 1]
  lastDraggableChild.component.exposed.focusPurchaseItemCombobox()
}

const newRoleServiceProviderMachineComponentRef = ref<typeof RoleServiceProviderMachineComponent>()
async function onNewServiceProviderMachineButtonClicked() {
  newRoleServiceProviderMachine.value = ModelFactory.createModel(RoleServiceProviderMachine, {
    roleServiceProvider: modelValue.value.id,
    sortIndex: getNextSortIndex()
  } as IRoleServiceProviderMachine)
  await nextTick()
  newRoleServiceProviderMachineComponentRef.value!.focusMachineCombobox()
}

function getNextSortIndex(): number {
  if (roleServiceProviderMachines.value.length === 0) {
    return 0
  }
  return Math.max(...roleServiceProviderMachines.value.map((rspm) => rspm.sortIndex)) + 1
}
</script>

<style scoped></style>
